/**
 * Generates crisp procedural vector/canvas textures for Zelvis Antigravity
 * Ensures the game works autonomously with zero missing assets or HTTP asset 404s.
 */
export function generateGameTextures(scene) {
  const textures = scene.textures;

  // HD Supersampling Hook: generates all procedural vector textures at 2x resolution
  // and marks source resolution = 2 so they render razor-sharp on high-DPI screens.
  const origCreateCanvas = textures.createCanvas.bind(textures);
  textures.createCanvas = function (key, width, height) {
    const scale = 2;
    const c = origCreateCanvas(key, width * scale, height * scale);
    const ctx = c.getContext();
    ctx.scale(scale, scale);
    return {
      getContext: () => ctx,
      refresh: () => {
        c.refresh();
        const tex = textures.get(key);
        if (tex && tex.getSourceImage()) {
          tex.getSourceImage().resolution = scale;
          const frame = tex.get('__BASE');
          if (frame) {
            frame.setSize(width, height);
          }
        }
      }
    };
  };

  // 1. ZELVIS DETAILED ANIMATION FRAMES (80x96 Canvas)
  const zelvisFrames = [
    'zelvis_idle',
    'zelvis_run_1',
    'zelvis_run_2',
    'zelvis_run_3',
    'zelvis_run_4',
    'zelvis_run_5',
    'zelvis_run_6',
    'zelvis_slide',
    'zelvis_skid',
    'zelvis_flip'
  ];

  zelvisFrames.forEach((key) => {
    if (!textures.exists(key)) {
      const canvas = textures.createCanvas(key, 80, 96);
      const ctx = canvas.getContext();
      drawDetailedZelvis(ctx, key);
      canvas.refresh();
    }
  });

  // Friction dust / smoke particle for slide and skid
  if (!textures.exists('particle_smoke')) {
    const canvas = textures.createCanvas('particle_smoke', 20, 20);
    const ctx = canvas.getContext();
    const grad = ctx.createRadialGradient(10, 10, 2, 10, 10, 9);
    grad.addColorStop(0, 'rgba(255, 235, 150, 0.9)');
    grad.addColorStop(0.5, 'rgba(200, 220, 240, 0.6)');
    grad.addColorStop(1, 'rgba(200, 220, 240, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(10, 10, 9, 0, Math.PI * 2);
    ctx.fill();
    canvas.refresh();
  }



  // Slide Button for Touch UI
  if (!textures.exists('btn_slide')) {
    const canvas = textures.createCanvas('btn_slide', 80, 80);
    const ctx = canvas.getContext();

    ctx.fillStyle = 'rgba(15, 23, 42, 0.8)';
    ctx.beginPath();
    ctx.arc(40, 40, 36, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(40, 40, 36, 0, Math.PI * 2);
    ctx.stroke();

    // Downward & forward slide chevron
    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.moveTo(26, 30);
    ctx.lineTo(44, 46);
    ctx.lineTo(62, 30);
    ctx.lineTo(54, 24);
    ctx.lineTo(44, 34);
    ctx.lineTo(34, 24);
    ctx.closePath();
    ctx.fill();

    // Text: SLIDE
    ctx.fillStyle = '#fde047';
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('SLIDE', 40, 64);

    canvas.refresh();
  }



  // Aegis Shield Forcefield Aura Texture
  if (!textures.exists('shield_aura')) {
    const canvas = textures.createCanvas('shield_aura', 110, 110);
    const ctx = canvas.getContext();
    const grad = ctx.createRadialGradient(55, 55, 25, 55, 55, 52);
    grad.addColorStop(0, 'rgba(56, 189, 248, 0.15)');
    grad.addColorStop(0.7, 'rgba(56, 189, 248, 0.45)');
    grad.addColorStop(0.9, 'rgba(250, 204, 21, 0.8)');
    grad.addColorStop(1, 'rgba(250, 204, 21, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(55, 55, 52, 0, Math.PI * 2);
    ctx.fill();

    // Electric arcs around perimeter
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(55, 55, 48, 0, Math.PI * 2);
    ctx.stroke();

    canvas.refresh();
  }



  // Overdrive electric lightning particle
  if (!textures.exists('particle_lightning')) {
    const canvas = textures.createCanvas('particle_lightning', 24, 24);
    const ctx = canvas.getContext();
    ctx.fillStyle = '#fef08a';
    ctx.shadowColor = '#38bdf8';
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.moveTo(14, 2);
    ctx.lineTo(6, 12);
    ctx.lineTo(12, 12);
    ctx.lineTo(8, 22);
    ctx.lineTo(18, 10);
    ctx.lineTo(12, 10);
    ctx.closePath();
    ctx.fill();
    canvas.refresh();
  }



  // 2. Marble Floor & Ceiling Platform
  if (!textures.exists('platform_marble')) {
    const canvas = textures.createCanvas('platform_marble', 120, 36);
    const ctx = canvas.getContext();
    
    // Marble gradient background
    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#f8f9fa');
    grad.addColorStop(0.5, '#e2e8f0');
    grad.addColorStop(1, '#cbd5e1');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.fill();

    // Golden Neon Border
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 3;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.stroke();

    // Greek meander pattern accent
    ctx.strokeStyle = '#d97706';
    ctx.lineWidth = 2;
    for (let x = 12; x < 108; x += 16) {
      ctx.beginPath();
      ctx.moveTo(x, 8);
      ctx.lineTo(x + 8, 8);
      ctx.lineTo(x + 8, 28);
      ctx.lineTo(x, 28);
      ctx.stroke();
    }

    canvas.refresh();
  }



  // 3. Floating Cloud Platform
  if (!textures.exists('platform_cloud')) {
    const canvas = textures.createCanvas('platform_cloud', 140, 40);
    const ctx = canvas.getContext();

    // Cloud bubbles
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(35, 25, 20, 0, Math.PI * 2);
    ctx.arc(70, 18, 24, 0, Math.PI * 2);
    ctx.arc(105, 25, 20, 0, Math.PI * 2);
    ctx.fill();

    // Cyan lightning glow underneath
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Golden lightning accent
    ctx.fillStyle = '#fbbf24';
    ctx.beginPath();
    ctx.moveTo(70, 8);
    ctx.lineTo(65, 20);
    ctx.lineTo(72, 20);
    ctx.lineTo(67, 34);
    ctx.lineTo(75, 18);
    ctx.lineTo(69, 18);
    ctx.closePath();
    ctx.fill();

    canvas.refresh();
  }



  // 4. Moving Electric Platform
  if (!textures.exists('platform_electric')) {
    const canvas = textures.createCanvas('platform_electric', 130, 32);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#1e1b4b';
    roundRect(ctx, 2, 2, 126, 28, 6);
    ctx.fill();

    ctx.strokeStyle = '#a855f7';
    ctx.lineWidth = 3;
    roundRect(ctx, 2, 2, 126, 28, 6);
    ctx.stroke();

    // Glowing core
    ctx.fillStyle = '#c084fc';
    ctx.fillRect(16, 12, 98, 8);

    canvas.refresh();
  }



  // 5. Golden Vinyl Collectible
  if (!textures.exists('item_vinyl')) {
    const canvas = textures.createCanvas('item_vinyl', 38, 38);
    const ctx = canvas.getContext();

    // Vinyl black outer disc
    ctx.fillStyle = '#18181b';
    ctx.beginPath();
    ctx.arc(19, 19, 17, 0, Math.PI * 2);
    ctx.fill();

    // Golden groove circles
    ctx.strokeStyle = '#facc15';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(19, 19, 14, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(19, 19, 10, 0, Math.PI * 2);
    ctx.stroke();

    // Golden center label
    ctx.fillStyle = '#eab308';
    ctx.beginPath();
    ctx.arc(19, 19, 6, 0, Math.PI * 2);
    ctx.fill();

    // Center hole
    ctx.fillStyle = '#09090b';
    ctx.beginPath();
    ctx.arc(19, 19, 2.5, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }



  // 6. Zeus Lightning Bolt Bonus
  if (!textures.exists('item_lightning')) {
    const canvas = textures.createCanvas('item_lightning', 36, 44);
    const ctx = canvas.getContext();

    ctx.shadowColor = '#fde047';
    ctx.shadowBlur = 8;
    ctx.fillStyle = '#fbbf24';
    ctx.beginPath();
    ctx.moveTo(22, 2);
    ctx.lineTo(8, 22);
    ctx.lineTo(18, 22);
    ctx.lineTo(12, 42);
    ctx.lineTo(28, 18);
    ctx.lineTo(18, 18);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    canvas.refresh();
  }



  // 7. Hazard: Thunder Spikes
  if (!textures.exists('hazard_spike')) {
    const canvas = textures.createCanvas('hazard_spike', 36, 40);
    const ctx = canvas.getContext();

    // Red-purple gradient
    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#ef4444');
    grad.addColorStop(1, '#7f1d1d');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#fca5a5';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }



  // 8. Hazard: Plasma Orb
  if (!textures.exists('hazard_orb')) {
    const canvas = textures.createCanvas('hazard_orb', 38, 38);
    const ctx = canvas.getContext();

    const radGrad = ctx.createRadialGradient(19, 19, 4, 19, 19, 18);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.3, '#f43f5e');
    radGrad.addColorStop(0.8, '#9f1239');
    radGrad.addColorStop(1, '#4c0519');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(19, 19, 16, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#fda4af';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(19, 19, 17, 0, Math.PI * 2);
    ctx.stroke();

    canvas.refresh();
  }



  // 8b. Tartarus Obsidian Platform
  if (!textures.exists('platform_tartarus')) {
    const canvas = textures.createCanvas('platform_tartarus', 120, 36);
    const ctx = canvas.getContext();

    // Dark obsidian gradient
    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#18022e');
    grad.addColorStop(0.5, '#090014');
    grad.addColorStop(1, '#2e0854');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.fill();

    // Electric purple neon border
    ctx.strokeStyle = '#c026d3';
    ctx.lineWidth = 3;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.stroke();

    // Glowing magenta cracks
    ctx.strokeStyle = '#f43f5e';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(15, 18);
    ctx.lineTo(35, 12);
    ctx.lineTo(55, 22);
    ctx.lineTo(85, 14);
    ctx.lineTo(105, 20);
    ctx.stroke();

    canvas.refresh();
  }



  // 8c. Hazard: Tartarus Spikes
  if (!textures.exists('hazard_tartarus_spike')) {
    const canvas = textures.createCanvas('hazard_tartarus_spike', 36, 40);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#e11d48');
    grad.addColorStop(0.5, '#9333ea');
    grad.addColorStop(1, '#3b0764');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#f43f5e';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }



  // 8d. Hazard: Laser Barrier
  if (!textures.exists('hazard_laser')) {
    const canvas = textures.createCanvas('hazard_laser', 24, 70);
    const ctx = canvas.getContext();

    // Emitters top and bottom
    ctx.fillStyle = '#475569';
    ctx.fillRect(4, 0, 16, 10);
    ctx.fillRect(4, 60, 16, 10);

    // Laser beam core
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(10, 10, 4, 50);

    // Neon magenta laser glow
    ctx.strokeStyle = '#e11d48';
    ctx.lineWidth = 3;
    ctx.strokeRect(9, 10, 6, 50);

    canvas.refresh();
  }



  // 8e. Finish Portal: Stargate
  if (!textures.exists('portal_finish')) {
    const canvas = textures.createCanvas('portal_finish', 80, 140);
    const ctx = canvas.getContext();

    // Portal arch
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.arc(40, 50, 32, Math.PI, 0, false);
    ctx.lineTo(72, 136);
    ctx.lineTo(8, 136);
    ctx.closePath();
    ctx.stroke();

    // Swirling energy vortex
    const radGrad = ctx.createRadialGradient(40, 60, 10, 40, 60, 36);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.4, '#38bdf8');
    radGrad.addColorStop(0.8, '#6366f1');
    radGrad.addColorStop(1, '#0f172a');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(40, 50, 28, Math.PI, 0, false);
    ctx.lineTo(68, 132);
    ctx.lineTo(12, 132);
    ctx.closePath();
    ctx.fill();

    // Lightning bolt in center
    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.moveTo(42, 35);
    ctx.lineTo(34, 55);
    ctx.lineTo(44, 55);
    ctx.lineTo(38, 75);
    ctx.lineTo(50, 50);
    ctx.lineTo(40, 50);
    ctx.closePath();
    ctx.fill();

    canvas.refresh();
  }



  // 8f. STAGE 3: Hephaestus Cyber Forge Bronze Platform
  if (!textures.exists('platform_bronze')) {
    const canvas = textures.createCanvas('platform_bronze', 120, 36);
    const ctx = canvas.getContext();

    // Dark bronze steel gradient
    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#78350f');
    grad.addColorStop(0.5, '#451a03');
    grad.addColorStop(1, '#b45309');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.fill();

    // Fiery orange glowing border
    ctx.strokeStyle = '#f97316';
    ctx.lineWidth = 3;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.stroke();

    // Molten heat lines & rivets
    ctx.fillStyle = '#fdba74';
    ctx.beginPath();
    ctx.arc(10, 10, 2.5, 0, Math.PI * 2);
    ctx.arc(110, 10, 2.5, 0, Math.PI * 2);
    ctx.arc(10, 26, 2.5, 0, Math.PI * 2);
    ctx.arc(110, 26, 2.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#ea580c';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(25, 18);
    ctx.lineTo(95, 18);
    ctx.stroke();

    canvas.refresh();
  }



  // 8g. STAGE 3: Floating Magma Platform
  if (!textures.exists('platform_magma')) {
    const canvas = textures.createCanvas('platform_magma', 130, 34);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#261204';
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.fill();

    ctx.strokeStyle = '#ffedd5';
    ctx.lineWidth = 2;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.stroke();

    // Magma core slit
    const magmaGrad = ctx.createLinearGradient(15, 0, 115, 0);
    magmaGrad.addColorStop(0, '#ef4444');
    magmaGrad.addColorStop(0.5, '#facc15');
    magmaGrad.addColorStop(1, '#ef4444');
    ctx.fillStyle = magmaGrad;
    ctx.fillRect(15, 12, 100, 10);

    canvas.refresh();
  }



  // 8h. STAGE 3: Hazard Forge Magma Spikes
  if (!textures.exists('hazard_forge_spike')) {
    const canvas = textures.createCanvas('hazard_forge_spike', 36, 40);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#fde047');
    grad.addColorStop(0.4, '#ea580c');
    grad.addColorStop(1, '#7c2d12');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#fbbf24';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }



  // 8i. STAGE 3: Hazard Rotating Saw Blade
  if (!textures.exists('hazard_saw')) {
    const canvas = textures.createCanvas('hazard_saw', 44, 44);
    const ctx = canvas.getContext();

    const cx = 22, cy = 22, r = 18;
    ctx.fillStyle = '#78350f';
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const aNext = ((i + 0.6) / 8) * Math.PI * 2;
      ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      ctx.lineTo(cx + Math.cos(aNext) * (r * 0.7), cy + Math.sin(aNext) * (r * 0.7));
    }
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#f97316';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Center rivet
    ctx.fillStyle = '#fef08a';
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }



  // 8j. STAGE 4: Cosmic Olympus Crystal Platform
  if (!textures.exists('platform_cosmic')) {
    const canvas = textures.createCanvas('platform_cosmic', 120, 36);
    const ctx = canvas.getContext();

    // Deep cosmic starlight gradient
    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#1e1b4b');
    grad.addColorStop(0.5, '#312e81');
    grad.addColorStop(1, '#0f172a');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.fill();

    // Shimmering cyan & golden cosmic border
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 3;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.stroke();

    // Starlight crystal inlays
    ctx.fillStyle = '#facc15';
    ctx.beginPath();
    ctx.moveTo(60, 8);
    ctx.lineTo(66, 18);
    ctx.lineTo(60, 28);
    ctx.lineTo(54, 18);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#e0f2fe';
    ctx.fillRect(16, 16, 26, 4);
    ctx.fillRect(78, 16, 26, 4);

    canvas.refresh();
  }



  // 8k. STAGE 4: Moving Cosmic Nebula Platform
  if (!textures.exists('platform_nebula')) {
    const canvas = textures.createCanvas('platform_nebula', 130, 36);
    const ctx = canvas.getContext();

    // Violet nebula clouds
    ctx.fillStyle = 'rgba(168, 85, 247, 0.7)';
    ctx.beginPath();
    ctx.arc(30, 20, 18, 0, Math.PI * 2);
    ctx.arc(65, 16, 22, 0, Math.PI * 2);
    ctx.arc(100, 20, 18, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#f472b6';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // Glowing stardust sparks
    ctx.fillStyle = '#fef08a';
    ctx.beginPath();
    ctx.arc(65, 12, 3, 0, Math.PI * 2);
    ctx.arc(45, 20, 2, 0, Math.PI * 2);
    ctx.arc(85, 20, 2, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }



  // 8l. STAGE 4: Hazard Cosmic Prism Spikes
  if (!textures.exists('hazard_cosmic_spike')) {
    const canvas = textures.createCanvas('hazard_cosmic_spike', 36, 40);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#f472b6');
    grad.addColorStop(0.5, '#818cf8');
    grad.addColorStop(1, '#312e81');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#c084fc';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }



  // 8m. STAGE 4: Hazard Pulsar Supernova Orb
  if (!textures.exists('hazard_supernova')) {
    const canvas = textures.createCanvas('hazard_supernova', 40, 40);
    const ctx = canvas.getContext();

    const radGrad = ctx.createRadialGradient(20, 20, 3, 20, 20, 18);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.3, '#38bdf8');
    radGrad.addColorStop(0.7, '#a855f7');
    radGrad.addColorStop(1, '#4c1d95');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(20, 20, 18, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#e0f2fe';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(20, 20, 19, 0, Math.PI * 2);
    ctx.stroke();

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 5: ATLANTIS NEON ABYSS TEXTURES
  // =========================================================================
  // 8n. STAGE 5: Bioluminescent Coral Platform
  if (!textures.exists('platform_coral')) {
    const canvas = textures.createCanvas('platform_coral', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#0e7490');
    grad.addColorStop(0.5, '#155e75');
    grad.addColorStop(1, '#083344');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.fill();

    ctx.strokeStyle = '#22d3ee';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.stroke();

    // Bioluminescent coral nodes
    ctx.fillStyle = '#67e8f9';
    ctx.beginPath();
    ctx.arc(30, 18, 4, 0, Math.PI * 2);
    ctx.arc(60, 18, 5, 0, Math.PI * 2);
    ctx.arc(90, 18, 4, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }

  // 8o. STAGE 5: Moving Hydro-Beam Platform
  if (!textures.exists('platform_hydro')) {
    const canvas = textures.createCanvas('platform_hydro', 130, 34);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(6, 182, 212, 0.4)');
    grad.addColorStop(0.5, 'rgba(34, 211, 238, 0.9)');
    grad.addColorStop(1, 'rgba(6, 182, 212, 0.4)');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 126, 30, 8);
    ctx.fill();

    ctx.strokeStyle = '#a5f3fc';
    ctx.lineWidth = 2;
    roundRect(ctx, 2, 2, 126, 30, 8);
    ctx.stroke();

    canvas.refresh();
  }

  // 8p. STAGE 5: Sea-Crystal Coral Spikes
  if (!textures.exists('hazard_coral_spike')) {
    const canvas = textures.createCanvas('hazard_coral_spike', 36, 40);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#67e8f9');
    grad.addColorStop(0.4, '#06b6d4');
    grad.addColorStop(1, '#083344');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#a5f3fc';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }

  // 8q. STAGE 5: Hazard Bioluminescent Jellyfish
  if (!textures.exists('hazard_jellyfish')) {
    const canvas = textures.createCanvas('hazard_jellyfish', 42, 42);
    const ctx = canvas.getContext();

    // Dome bell
    const radGrad = ctx.createRadialGradient(21, 16, 2, 21, 16, 16);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.4, '#22d3ee');
    radGrad.addColorStop(0.8, '#0891b2');
    radGrad.addColorStop(1, '#164e63');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(21, 18, 16, Math.PI, 0, false);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#cffafe';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Shock tentacles
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(10, 18); ctx.lineTo(8, 36);
    ctx.moveTo(16, 18); ctx.lineTo(15, 39);
    ctx.moveTo(21, 18); ctx.lineTo(21, 40);
    ctx.moveTo(26, 18); ctx.lineTo(27, 39);
    ctx.moveTo(32, 18); ctx.lineTo(34, 36);
    ctx.stroke();

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 6: DIONYSUS NEON RAVE TEXTURES
  // =========================================================================
  // 8r. STAGE 6: Cyber Grape Vineyard Platform
  if (!textures.exists('platform_grape')) {
    const canvas = textures.createCanvas('platform_grape', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#581c87');
    grad.addColorStop(0.5, '#3b0764');
    grad.addColorStop(1, '#1e0533');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.fill();

    ctx.strokeStyle = '#facc15';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.stroke();

    // Neon violet vine nodes
    ctx.fillStyle = '#e879f9';
    ctx.beginPath();
    ctx.arc(25, 18, 5, 0, Math.PI * 2);
    ctx.arc(45, 18, 4, 0, Math.PI * 2);
    ctx.arc(75, 18, 4, 0, Math.PI * 2);
    ctx.arc(95, 18, 5, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }

  // 8s. STAGE 6: Moving Golden Rave Stage Platform
  if (!textures.exists('platform_goldrave')) {
    const canvas = textures.createCanvas('platform_goldrave', 130, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#fef08a');
    grad.addColorStop(0.5, '#f59e0b');
    grad.addColorStop(1, '#78350f');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 126, 32, 6);
    ctx.fill();

    ctx.strokeStyle = '#d946ef';
    ctx.lineWidth = 3;
    roundRect(ctx, 2, 2, 126, 32, 6);
    ctx.stroke();

    canvas.refresh();
  }

  // 8t. STAGE 6: Neon Rave Party Spikes
  if (!textures.exists('hazard_rave_spike')) {
    const canvas = textures.createCanvas('hazard_rave_spike', 36, 40);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#f43f5e');
    grad.addColorStop(0.5, '#d946ef');
    grad.addColorStop(1, '#3b0764');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }

  // 8u. STAGE 6: Hazard Bass Soundwave Speaker
  if (!textures.exists('hazard_speaker')) {
    const canvas = textures.createCanvas('hazard_speaker', 44, 44);
    const ctx = canvas.getContext();

    // Outer dark housing
    ctx.fillStyle = '#1e1b4b';
    roundRect(ctx, 2, 2, 40, 40, 8);
    ctx.fill();
    ctx.strokeStyle = '#facc15';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 40, 40, 8);
    ctx.stroke();

    // Speaker cone with soundwave glow
    const radGrad = ctx.createRadialGradient(22, 22, 2, 22, 22, 15);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.4, '#e879f9');
    radGrad.addColorStop(0.8, '#a21caf');
    radGrad.addColorStop(1, '#4c0519');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(22, 22, 14, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 7: ARES ORBITAL WAR CITADEL TEXTURES
  // =========================================================================
  // 8v. STAGE 7: Heavy Titanium Armor Platform
  if (!textures.exists('platform_titanium')) {
    const canvas = textures.createCanvas('platform_titanium', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#27272a');
    grad.addColorStop(0.5, '#18181b');
    grad.addColorStop(1, '#09090b');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 4);
    ctx.fill();

    // Combat warning crimson border
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 116, 32, 4);
    ctx.stroke();

    // Rivet bolts
    ctx.fillStyle = '#dc2626';
    ctx.fillRect(10, 8, 4, 4);
    ctx.fillRect(106, 8, 4, 4);
    ctx.fillRect(10, 24, 4, 4);
    ctx.fillRect(106, 24, 4, 4);

    canvas.refresh();
  }

  // 8w. STAGE 7: Moving Plasma Conduit Platform
  if (!textures.exists('platform_plasma')) {
    const canvas = textures.createCanvas('platform_plasma', 130, 34);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(239, 68, 68, 0.4)');
    grad.addColorStop(0.5, 'rgba(249, 115, 22, 0.95)');
    grad.addColorStop(1, 'rgba(239, 68, 68, 0.4)');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.fill();

    ctx.strokeStyle = '#fef08a';
    ctx.lineWidth = 2;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.stroke();

    canvas.refresh();
  }

  // 8x. STAGE 7: Serrated Combat Titanium Spikes
  if (!textures.exists('hazard_ares_spike')) {
    const canvas = textures.createCanvas('hazard_ares_spike', 36, 40);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#f87171');
    grad.addColorStop(0.4, '#b91c1c');
    grad.addColorStop(1, '#450a0a');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#fca5a5';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }

  // 8y. STAGE 7: Hazard Orbital Magnetic Mine
  if (!textures.exists('hazard_mine')) {
    const canvas = textures.createCanvas('hazard_mine', 44, 44);
    const ctx = canvas.getContext();

    const radGrad = ctx.createRadialGradient(22, 22, 3, 22, 22, 18);
    radGrad.addColorStop(0, '#ef4444');
    radGrad.addColorStop(0.4, '#991b1b');
    radGrad.addColorStop(0.8, '#27272a');
    radGrad.addColorStop(1, '#09090b');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(22, 22, 16, 0, Math.PI * 2);
    ctx.fill();

    // Spikes around mine
    ctx.strokeStyle = '#f87171';
    ctx.lineWidth = 3;
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI * 2) / 8;
      ctx.beginPath();
      ctx.moveTo(22 + Math.cos(angle) * 15, 22 + Math.sin(angle) * 15);
      ctx.lineTo(22 + Math.cos(angle) * 21, 22 + Math.sin(angle) * 21);
      ctx.stroke();
    }

    // Glowing blinking core
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(22, 22, 4, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 8: KRONOS CHRONO-SINGULARITY TEXTURES
  // =========================================================================
  // 8z. STAGE 8: Golden Clockwork Platform
  if (!textures.exists('platform_chronos')) {
    const canvas = textures.createCanvas('platform_chronos', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#fef08a');
    grad.addColorStop(0.3, '#d97706');
    grad.addColorStop(0.7, '#78350f');
    grad.addColorStop(1, '#31104b');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.fill();

    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 3;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.stroke();

    // Gear tooth cutouts on platform edges
    ctx.fillStyle = '#fde047';
    for (let x = 16; x < 110; x += 22) {
      ctx.fillRect(x, 14, 8, 8);
    }

    canvas.refresh();
  }

  // 8aa. STAGE 8: Moving Spacetime Rift Platform
  if (!textures.exists('platform_rift')) {
    const canvas = textures.createCanvas('platform_rift', 130, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(168, 85, 247, 0.7)');
    grad.addColorStop(0.5, 'rgba(253, 224, 71, 0.95)');
    grad.addColorStop(1, 'rgba(56, 189, 248, 0.7)');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 126, 32, 8);
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 32, 8);
    ctx.stroke();

    canvas.refresh();
  }

  // 8ab. STAGE 8: Temporal Shard Spikes
  if (!textures.exists('hazard_chronos_spike')) {
    const canvas = textures.createCanvas('hazard_chronos_spike', 36, 40);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#fde047');
    grad.addColorStop(0.4, '#a855f7');
    grad.addColorStop(1, '#31104b');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 9: RIVER STYX SPECTRAL RUN TEXTURES
  // =========================================================================
  // 8ad. STAGE 9: Spectral Bone-Jade Stone Platform
  if (!textures.exists('platform_styx')) {
    const canvas = textures.createCanvas('platform_styx', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#047857');
    grad.addColorStop(0.5, '#064e3b');
    grad.addColorStop(1, '#022c22');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.fill();

    ctx.strokeStyle = '#34d399';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.stroke();

    // Spectral rune glyphs
    ctx.fillStyle = '#a7f3d0';
    ctx.beginPath();
    ctx.arc(30, 18, 4, 0, Math.PI * 2);
    ctx.arc(60, 18, 5, 0, Math.PI * 2);
    ctx.arc(90, 18, 4, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }

  // 8ae. STAGE 9: Moving Ghost Barge Ferry Platform
  if (!textures.exists('platform_ferry')) {
    const canvas = textures.createCanvas('platform_ferry', 130, 34);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(6, 78, 59, 0.7)');
    grad.addColorStop(0.5, 'rgba(16, 185, 129, 0.95)');
    grad.addColorStop(1, 'rgba(6, 78, 59, 0.7)');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.fill();

    ctx.strokeStyle = '#6ee7b7';
    ctx.lineWidth = 2;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.stroke();

    canvas.refresh();
  }

  // 8af. STAGE 9: Ghost-Fire Jade Crystal Spikes
  if (!textures.exists('hazard_styx_spike')) {
    const canvas = textures.createCanvas('hazard_styx_spike', 36, 40);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#6ee7b7');
    grad.addColorStop(0.4, '#10b981');
    grad.addColorStop(1, '#022c22');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#a7f3d0';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }

  // 8ag. STAGE 9: Hazard Gliding Ghost Specter
  if (!textures.exists('hazard_ghost')) {
    const canvas = textures.createCanvas('hazard_ghost', 44, 44);
    const ctx = canvas.getContext();

    // Floating Wraith Skull
    const radGrad = ctx.createRadialGradient(22, 18, 2, 22, 18, 16);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.3, '#34d399');
    radGrad.addColorStop(0.7, '#059669');
    radGrad.addColorStop(1, '#022c22');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(22, 18, 14, 0, Math.PI * 2);
    ctx.fill();

    // Ghost trails
    ctx.fillStyle = '#10b981';
    ctx.beginPath();
    ctx.moveTo(10, 22); ctx.lineTo(8, 40); ctx.lineTo(16, 26);
    ctx.lineTo(22, 42); ctx.lineTo(28, 26); ctx.lineTo(36, 40); ctx.lineTo(34, 22);
    ctx.fill();

    // Glowing eyes
    ctx.fillStyle = '#01140e';
    ctx.beginPath();
    ctx.arc(17, 16, 3, 0, Math.PI * 2);
    ctx.arc(27, 16, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#a7f3d0';
    ctx.beginPath();
    ctx.arc(17, 16, 1.5, 0, Math.PI * 2);
    ctx.arc(27, 16, 1.5, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 10: APOLLO SOLAR HYPER-DRIVE TEXTURES
  // =========================================================================
  // 8ah. STAGE 10: Blazing Sunfire Alloy Platform
  if (!textures.exists('platform_solar')) {
    const canvas = textures.createCanvas('platform_solar', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#f97316');
    grad.addColorStop(0.5, '#ea580c');
    grad.addColorStop(1, '#7c2d12');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.fill();

    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 3;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.stroke();

    // Solar flare inlays
    ctx.fillStyle = '#fef08a';
    ctx.beginPath();
    ctx.arc(60, 18, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(20, 16, 24, 4);
    ctx.fillRect(76, 16, 24, 4);

    canvas.refresh();
  }

  // 8ai. STAGE 10: Moving Fusion Plasma Beam Platform
  if (!textures.exists('platform_sunspot')) {
    const canvas = textures.createCanvas('platform_sunspot', 130, 34);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(234, 88, 12, 0.5)');
    grad.addColorStop(0.5, 'rgba(254, 240, 138, 0.95)');
    grad.addColorStop(1, 'rgba(234, 88, 12, 0.5)');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.stroke();

    canvas.refresh();
  }

  // 8aj. STAGE 10: Prominence Solar Plasma Spikes
  if (!textures.exists('hazard_solar_spike')) {
    const canvas = textures.createCanvas('hazard_solar_spike', 36, 40);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.3, '#fde047');
    grad.addColorStop(0.7, '#ea580c');
    grad.addColorStop(1, '#431407');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#fef08a';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }

  // 8ak. STAGE 10: Hazard Spinning Solar Flare Orb
  if (!textures.exists('hazard_sunflare')) {
    const canvas = textures.createCanvas('hazard_sunflare', 44, 44);
    const ctx = canvas.getContext();

    const radGrad = ctx.createRadialGradient(22, 22, 3, 22, 22, 16);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.4, '#fde047');
    radGrad.addColorStop(0.8, '#ea580c');
    radGrad.addColorStop(1, '#7c2d12');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(22, 22, 15, 0, Math.PI * 2);
    ctx.fill();

    // 4 spinning flare prominences
    ctx.strokeStyle = '#fef08a';
    ctx.lineWidth = 3;
    for (let i = 0; i < 4; i++) {
      const angle = (i * Math.PI) / 2;
      ctx.beginPath();
      ctx.moveTo(22 + Math.cos(angle) * 12, 22 + Math.sin(angle) * 12);
      ctx.quadraticCurveTo(
        22 + Math.cos(angle + 0.5) * 24,
        22 + Math.sin(angle + 0.5) * 24,
        22 + Math.cos(angle) * 20,
        22 + Math.sin(angle) * 20
      );
      ctx.stroke();
    }

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 11: CHAOS PRIMORDIAL GENESIS TEXTURES
  // =========================================================================
  // 8al. STAGE 11: Quantum Antimatter Shard Platform
  if (!textures.exists('platform_chaos')) {
    const canvas = textures.createCanvas('platform_chaos', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 120, 36);
    grad.addColorStop(0, '#ec4899');
    grad.addColorStop(0.35, '#8b5cf6');
    grad.addColorStop(0.7, '#3b82f6');
    grad.addColorStop(1, '#020617');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 116, 32, 6);
    ctx.stroke();

    // Shifting prismatic inlays
    ctx.fillStyle = '#facc15';
    ctx.beginPath();
    ctx.moveTo(60, 6); ctx.lineTo(68, 18); ctx.lineTo(60, 30); ctx.lineTo(52, 18);
    ctx.closePath();
    ctx.fill();

    canvas.refresh();
  }

  // 8am. STAGE 11: Moving Cosmic String Ribbon Platform
  if (!textures.exists('platform_genesis')) {
    const canvas = textures.createCanvas('platform_genesis', 130, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(236, 72, 153, 0.7)');
    grad.addColorStop(0.5, 'rgba(250, 204, 21, 0.95)');
    grad.addColorStop(1, 'rgba(59, 130, 246, 0.7)');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 126, 32, 8);
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    roundRect(ctx, 2, 2, 126, 32, 8);
    ctx.stroke();

    canvas.refresh();
  }

  // 8an. STAGE 11: Reality-Bending Prismatic Spikes
  if (!textures.exists('hazard_chaos_spike')) {
    const canvas = textures.createCanvas('hazard_chaos_spike', 36, 40);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 40);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.25, '#f43f5e');
    grad.addColorStop(0.6, '#8b5cf6');
    grad.addColorStop(1, '#020617');

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(18, 2);
    ctx.lineTo(34, 38);
    ctx.lineTo(2, 38);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    canvas.refresh();
  }

  // 8ao. STAGE 11: Hazard Antimatter Singularity Core
  if (!textures.exists('hazard_antimatter')) {
    const canvas = textures.createCanvas('hazard_antimatter', 44, 44);
    const ctx = canvas.getContext();

    // Event horizon outer ring
    const radGrad = ctx.createRadialGradient(22, 22, 6, 22, 22, 20);
    radGrad.addColorStop(0, '#000000');
    radGrad.addColorStop(0.4, '#1e1b4b');
    radGrad.addColorStop(0.7, '#ec4899');
    radGrad.addColorStop(1, '#38bdf8');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(22, 22, 18, 0, Math.PI * 2);
    ctx.fill();

    // Black Hole Pure Darkness center
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(22, 22, 9, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    canvas.refresh();
  }



  // 9. Particle: Spark / Star
  if (!textures.exists('particle_star')) {
    const canvas = textures.createCanvas('particle_star', 16, 16);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.arc(8, 8, 5, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }



  // 10. Particle: Music Note
  if (!textures.exists('particle_note')) {
    const canvas = textures.createCanvas('particle_note', 24, 24);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#67e8f9';
    ctx.font = 'bold 20px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('♪', 12, 12);

    canvas.refresh();
  }



  // 11. UI Button: Anti-Gravity Button
  if (!textures.exists('btn_antigravity')) {
    const canvas = textures.createCanvas('btn_antigravity', 120, 120);
    const ctx = canvas.getContext();

    // Outer glow ring
    const radGrad = ctx.createRadialGradient(60, 60, 40, 60, 60, 58);
    radGrad.addColorStop(0, '#f59e0b');
    radGrad.addColorStop(0.7, '#ef4444');
    radGrad.addColorStop(1, '#7c3aed');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(60, 60, 56, 0, Math.PI * 2);
    ctx.fill();

    // Inner disc
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(60, 60, 46, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#fbbf24';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(60, 60, 46, 0, Math.PI * 2);
    ctx.stroke();

    // Inverted double arrows (Up & Down)
    ctx.fillStyle = '#ffffff';
    // Up arrow
    ctx.beginPath();
    ctx.moveTo(60, 24);
    ctx.lineTo(44, 42);
    ctx.lineTo(76, 42);
    ctx.closePath();
    ctx.fill();

    // Down arrow
    ctx.beginPath();
    ctx.moveTo(60, 96);
    ctx.lineTo(44, 78);
    ctx.lineTo(76, 78);
    ctx.closePath();
    ctx.fill();

    // Center Lightning Bolt
    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.moveTo(63, 44);
    ctx.lineTo(52, 62);
    ctx.lineTo(62, 62);
    ctx.lineTo(57, 76);
    ctx.lineTo(69, 58);
    ctx.lineTo(59, 58);
    ctx.closePath();
    ctx.fill();

    canvas.refresh();
  }



  // 12. UI Button: Left Arrow
  if (!textures.exists('btn_arrow_left')) {
    const canvas = textures.createCanvas('btn_arrow_left', 80, 80);
    const ctx = canvas.getContext();

    ctx.fillStyle = 'rgba(15, 23, 42, 0.75)';
    ctx.beginPath();
    ctx.arc(40, 40, 36, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(40, 40, 36, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = '#38bdf8';
    ctx.beginPath();
    ctx.moveTo(25, 40);
    ctx.lineTo(52, 22);
    ctx.lineTo(52, 58);
    ctx.closePath();
    ctx.fill();

    canvas.refresh();
  }



  // 13. UI Button: Right Arrow
  if (!textures.exists('btn_arrow_right')) {
    const canvas = textures.createCanvas('btn_arrow_right', 80, 80);
    const ctx = canvas.getContext();

    ctx.fillStyle = 'rgba(15, 23, 42, 0.75)';
    ctx.beginPath();
    ctx.arc(40, 40, 36, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(40, 40, 36, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = '#38bdf8';
    ctx.beginPath();
    ctx.moveTo(55, 40);
    ctx.lineTo(28, 22);
    ctx.lineTo(28, 58);
    ctx.closePath();
    ctx.fill();

    canvas.refresh();
  }

  // Restore original createCanvas
  textures.createCanvas = origCreateCanvas;
}

/**
 * Detailed procedural rendering for Zelvis (King of Rock 'n Olympus)
 * Supports: idle, run_1..6, slide (rockstar knee slide), skid (braking), and flip (mid-air)
 */
function drawDetailedZelvis(ctx, key) {
  ctx.save();
  ctx.clearRect(0, 0, 80, 96);

  const isSlide = key === 'zelvis_slide';
  const isSkid = key === 'zelvis_skid';
  const isFlip = key === 'zelvis_flip';
  const isIdle = key === 'zelvis_idle';

  let runFrame = 0;
  if (key.startsWith('zelvis_run_')) {
    runFrame = parseInt(key.replace('zelvis_run_', ''), 10);
  }

  // 1. CAPE RENDERING (Dynamic flowing red velvet cape with shading and gold trim)
  ctx.save();
  drawDetailedCape(ctx, key, runFrame, isSlide, isSkid, isFlip);
  ctx.restore();

  // 2. BODY & LIMBS
  if (isSlide) {
    // === ROCKSTAR KNEE SLIDE POSE ===
    // Body reclined backward at 45 degrees, sliding horizontally on knees
    ctx.save();
    // Sliding legs on knees
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;

    // Both legs tucked under, sliding on shins
    ctx.beginPath();
    ctx.moveTo(35, 62);
    ctx.lineTo(15, 84);
    ctx.lineTo(40, 86);
    ctx.lineTo(52, 70);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Golden boots tucked back
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(8, 80, 16, 8);
    // Gold lightning trim on legs
    ctx.strokeStyle = '#fbbf24';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(25, 72);
    ctx.lineTo(45, 76);
    ctx.stroke();

    // Reclined torso
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(34, 58);
    ctx.lineTo(58, 48);
    ctx.lineTo(66, 62);
    ctx.lineTo(42, 72);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Gold belt
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(38, 64, 16, 6);
    ctx.fillStyle = '#fde047';
    ctx.fillRect(44, 63, 6, 8);

    // Left arm extended back in rock sign
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(24, 50, 14, 6);

    // Right arm reaching forward with golden microphone
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(56, 52);
    ctx.lineTo(72, 44);
    ctx.lineTo(70, 50);
    ctx.lineTo(54, 58);
    ctx.closePath();
    ctx.fill();

    // Microphone
    drawMicrophone(ctx, 74, 42, -0.2);

    // Head tilted up triumphantly
    drawHead(ctx, 58, 32, true);
    ctx.restore();

  } else if (isSkid) {
    // === ROCKSTAR BRAKING / SKID POSE ===
    // 3/4 turn, heels dug in, arms stabilizing, torso braced
    ctx.save();
    // Braced flared legs
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;

    // Left leg back bracing
    ctx.beginPath();
    ctx.moveTo(34, 54);
    ctx.lineTo(18, 86);
    ctx.lineTo(28, 88);
    ctx.lineTo(40, 56);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Right leg forward digging heel
    ctx.beginPath();
    ctx.moveTo(38, 54);
    ctx.lineTo(52, 85);
    ctx.lineTo(62, 83);
    ctx.lineTo(46, 56);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Gold boots
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(14, 84, 16, 8);
    ctx.fillRect(52, 81, 16, 8);

    // Gold lightning on trousers
    drawTrousersLightning(ctx, 22, 68, 54, 68);

    // Torso leaned back against momentum
    drawTorso(ctx, 30, 34, 22, 24);

    // Belt
    drawBelt(ctx, 30, 54, 22);

    // Arms spread wide for balance
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(18, 38, 14, 6); // left arm back
    ctx.fillRect(48, 38, 16, 6); // right arm forward with mic
    drawMicrophone(ctx, 66, 36, 0.3);

    // Head focused ahead
    drawHead(ctx, 41, 24, false);
    ctx.restore();

  } else if (isFlip) {
    // === ANTI-GRAVITY MIDAIR FLIP POSE ===
    ctx.save();
    // Tucked athletic legs
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.moveTo(34, 52);
    ctx.lineTo(24, 70);
    ctx.lineTo(34, 74);
    ctx.lineTo(40, 56);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(42, 52);
    ctx.lineTo(52, 70);
    ctx.lineTo(44, 74);
    ctx.lineTo(36, 56);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Boots
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(22, 68, 14, 7);
    ctx.fillRect(42, 68, 14, 7);

    // Torso
    drawTorso(ctx, 30, 32, 22, 24);
    drawBelt(ctx, 30, 52, 22);

    // Arms outstretched heroically
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(14, 34, 16, 6);
    ctx.fillRect(50, 32, 16, 6);
    drawMicrophone(ctx, 68, 30, -0.4);

    drawHead(ctx, 41, 22, false);

    // Zeus Electric Aura circles
    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(41, 46, 34, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();

  } else {
    // === RUN CYCLE (6 FRAMES) OR IDLE ===
    ctx.save();

    // Legs position per run frame
    drawRunLegs(ctx, runFrame, isIdle);

    // Torso with slight bob
    const bob = (runFrame === 2 || runFrame === 5) ? 2 : 0;
    drawTorso(ctx, 31, 33 + bob, 22, 24);
    drawBelt(ctx, 31, 53 + bob, 22);

    // Arms & Golden Microphone swinging with stride
    drawRunArms(ctx, runFrame, isIdle, bob);

    // Head
    drawHead(ctx, 42, 23 + bob, false);

    ctx.restore();
  }

  ctx.restore();
}

/**
 * Dynamic Flowing Cape with Satin Folds & Shading
 */
function drawDetailedCape(ctx, key, runFrame, isSlide, isSkid, isFlip) {
  // Cape gradient
  const grad = ctx.createLinearGradient(10, 20, 70, 80);
  grad.addColorStop(0, '#ef4444');
  grad.addColorStop(0.5, '#dc2626');
  grad.addColorStop(1, '#991b1b');
  ctx.fillStyle = grad;

  ctx.beginPath();
  if (isSlide) {
    // Horizontal trailing cape
    ctx.moveTo(35, 52);
    ctx.quadraticCurveTo(15, 48, 2, 54);
    ctx.lineTo(4, 68);
    ctx.quadraticCurveTo(20, 64, 40, 68);
  } else if (isSkid) {
    // Cape whipping around from braking
    ctx.moveTo(36, 34);
    ctx.quadraticCurveTo(10, 20, 8, 48);
    ctx.lineTo(20, 66);
    ctx.lineTo(34, 48);
  } else if (isFlip) {
    // Wide billowing circular cape
    ctx.moveTo(30, 32);
    ctx.quadraticCurveTo(4, 38, 6, 75);
    ctx.lineTo(26, 70);
    ctx.lineTo(36, 46);
  } else {
    // Dynamic run frames (billowing up and down)
    const wave = Math.sin((runFrame || 1) * Math.PI / 3) * 8;
    ctx.moveTo(30, 34);
    ctx.quadraticCurveTo(10 - wave, 48, 8, 72 + wave);
    ctx.lineTo(24, 68);
    ctx.lineTo(35, 42);
  }
  ctx.closePath();
  ctx.fill();

  // Gold trim on cape edge
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

/**
 * 6-Frame Running Leg Kinematics with Bell-Bottom Flares & Gold Embroidery
 */
function drawRunLegs(ctx, frame, isIdle) {
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.5;

  let leftX = 26, leftY = 86;
  let rightX = 48, rightY = 86;

  if (isIdle) {
    leftX = 32; rightX = 46;
  } else {
    switch (frame) {
      case 1: leftX = 20; rightX = 54; break;
      case 2: leftX = 28; rightX = 46; break;
      case 3: leftX = 36; rightX = 38; break;
      case 4: leftX = 54; rightX = 20; break;
      case 5: leftX = 46; rightX = 28; break;
      case 6: leftX = 38; rightX = 36; break;
    }
  }

  // Left Leg
  ctx.beginPath();
  ctx.moveTo(34, 55);
  ctx.lineTo(leftX - 4, leftY - 4);
  ctx.lineTo(leftX + 8, leftY - 4);
  ctx.lineTo(40, 55);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Right Leg
  ctx.beginPath();
  ctx.moveTo(42, 55);
  ctx.lineTo(rightX - 4, rightY - 4);
  ctx.lineTo(rightX + 8, rightY - 4);
  ctx.lineTo(48, 55);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Golden boots
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(leftX - 6, leftY - 4, 16, 8);
  ctx.fillRect(rightX - 6, rightY - 4, 16, 8);

  // Boots lightning trim
  ctx.fillStyle = '#fde047';
  ctx.fillRect(leftX + 2, leftY - 4, 4, 4);
  ctx.fillRect(rightX + 2, rightY - 4, 4, 4);

  // Bell-bottom gold lightning piping
  drawTrousersLightning(ctx, leftX + 2, leftY - 14, rightX + 2, rightY - 14);
}

function drawTrousersLightning(ctx, x1, y1, x2, y2) {
  ctx.strokeStyle = '#fbbf24';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(x1, y1 - 10);
  ctx.lineTo(x1 - 2, y1 - 4);
  ctx.lineTo(x1 + 2, y1);
  ctx.moveTo(x2, y2 - 10);
  ctx.lineTo(x2 + 2, y2 - 4);
  ctx.lineTo(x2 - 2, y2);
  ctx.stroke();
}

/**
 * Detailed Torso with White Suit, Open V-Neck & Gold Chest Lightning
 */
function drawTorso(ctx, x, y, w, h) {
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.5;
  roundRect(ctx, x, y, w, h, 4);
  ctx.fill();
  ctx.stroke();

  // Open chest V-neck (skin)
  ctx.fillStyle = '#fed7aa';
  ctx.beginPath();
  ctx.moveTo(x + 6, y);
  ctx.lineTo(x + w - 6, y);
  ctx.lineTo(x + w / 2, y + 10);
  ctx.closePath();
  ctx.fill();

  // Gold chain / Zeus medallion
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(x + w / 2, y + 6, 3, 0, Math.PI);
  ctx.stroke();

  // Golden Zeus Lightning Bolt on chest
  ctx.fillStyle = '#eab308';
  ctx.beginPath();
  ctx.moveTo(x + w / 2 + 2, y + 8);
  ctx.lineTo(x + w / 2 - 4, y + 15);
  ctx.lineTo(x + w / 2 + 1, y + 15);
  ctx.lineTo(x + w / 2 - 2, y + 21);
  ctx.lineTo(x + w / 2 + 5, y + 13);
  ctx.lineTo(x + w / 2, y + 13);
  ctx.closePath();
  ctx.fill();
}

/**
 * Gold Eagle Belt
 */
function drawBelt(ctx, x, y, w) {
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(x - 1, y, w + 2, 5);
  // Gold Eagle Buckle
  ctx.fillStyle = '#fde047';
  ctx.fillRect(x + w / 2 - 4, y - 1, 8, 7);
  ctx.fillStyle = '#b45309';
  ctx.fillRect(x + w / 2 - 2, y + 1, 4, 3);
}

/**
 * Running Arms with Microphone Swings
 */
function drawRunArms(ctx, frame, isIdle, bob) {
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.5;

  let micX = 58, micY = 38 + bob;
  let leftArmX = 20, leftArmY = 40 + bob;

  if (frame === 1 || frame === 2) {
    micX = 64; micY = 32 + bob;
    leftArmX = 18; leftArmY = 44 + bob;
  } else if (frame === 4 || frame === 5) {
    micX = 52; micY = 42 + bob;
    leftArmX = 26; leftArmY = 34 + bob;
  }

  // Left arm
  ctx.beginPath();
  ctx.moveTo(32, 36 + bob);
  ctx.lineTo(leftArmX, leftArmY);
  ctx.lineTo(leftArmX + 4, leftArmY + 4);
  ctx.lineTo(34, 42 + bob);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Right arm holding golden mic
  ctx.beginPath();
  ctx.moveTo(50, 36 + bob);
  ctx.lineTo(micX - 4, micY);
  ctx.lineTo(micX, micY + 4);
  ctx.lineTo(52, 42 + bob);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Golden microphone
  drawMicrophone(ctx, micX + 2, micY - 4, 0.1);
}

/**
 * Detailed Golden Microphone with Sparkling Head
 */
function drawMicrophone(ctx, x, y, angle = 0) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);

  // Handle
  ctx.fillStyle = '#d97706';
  ctx.fillRect(-2, 0, 4, 12);
  ctx.strokeStyle = '#78350f';
  ctx.lineWidth = 1;
  ctx.strokeRect(-2, 0, 4, 12);

  // Spherical Golden Grill Head
  const grad = ctx.createRadialGradient(-1, -4, 1, 0, -4, 6);
  grad.addColorStop(0, '#fef08a');
  grad.addColorStop(0.5, '#facc15');
  grad.addColorStop(1, '#ca8a04');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(0, -4, 6, 0, Math.PI * 2);
  ctx.fill();

  // Mesh lines
  ctx.strokeStyle = '#a16207';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(-5, -4);
  ctx.lineTo(5, -4);
  ctx.moveTo(0, -9);
  ctx.lineTo(0, 1);
  ctx.stroke();

  // Gleam spark
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-2, -7, 2, 2);

  ctx.restore();
}

/**
 * Detailed Head: Zeus White Beard, Golden Aviator Sunglasses, White Pompadour Hair
 */
function drawHead(ctx, x, y, lookingUp = false) {
  ctx.save();
  ctx.translate(x, y);

  // 1. Skin Face
  ctx.fillStyle = '#fed7aa';
  ctx.beginPath();
  ctx.arc(0, 0, 9, 0, Math.PI * 2);
  ctx.fill();

  // 2. Majestic Zeus White Beard & Mustache
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(-8, 0);
  ctx.quadraticCurveTo(-10, 10, -4, 15);
  ctx.quadraticCurveTo(0, 18, 4, 15);
  ctx.quadraticCurveTo(10, 10, 8, 0);
  ctx.lineTo(6, 4);
  ctx.quadraticCurveTo(0, 8, -6, 4);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Curled beard locks detail
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(-3, 6);
  ctx.lineTo(-2, 13);
  ctx.moveTo(2, 6);
  ctx.lineTo(2, 13);
  ctx.stroke();

  // 3. Golden Mirrored Aviator Sunglasses
  const glassY = lookingUp ? -4 : -2;
  // Left lens
  ctx.fillStyle = '#1e1b4b';
  roundRect(ctx, -7, glassY, 6, 5, 1.5);
  ctx.fill();
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  roundRect(ctx, -7, glassY, 6, 5, 1.5);
  ctx.stroke();

  // Right lens
  ctx.fillStyle = '#1e1b4b';
  roundRect(ctx, 1, glassY, 6, 5, 1.5);
  ctx.fill();
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  roundRect(ctx, 1, glassY, 6, 5, 1.5);
  ctx.stroke();

  // Bridge
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(-1, glassY + 1);
  ctx.lineTo(1, glassY + 1);
  ctx.stroke();

  // White lens reflection glare
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-5, glassY + 1);
  ctx.lineTo(-3, glassY + 4);
  ctx.moveTo(3, glassY + 1);
  ctx.lineTo(5, glassY + 4);
  ctx.stroke();

  // 4. Signature Elvis White Pompadour Hair
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(-8, -4);
  ctx.quadraticCurveTo(-10, -15, 0, -16);
  ctx.quadraticCurveTo(10, -16, 9, -5);
  ctx.quadraticCurveTo(4, -9, -4, -7);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Hair wave highlight
  ctx.strokeStyle = '#f1f5f9';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-4, -13);
  ctx.quadraticCurveTo(2, -14, 6, -10);
  ctx.stroke();

  ctx.restore();
}

function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}
